import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { HeaderThemeProvider, useHeaderThemeContext } from './contexts/HeaderThemeContext';

// Hook to get selected addons from URL
function useSelectedAddons() {
  const [selectedAddons, setSelectedAddons] = React.useState<string[]>([]);

  React.useEffect(() => {
    const updateAddons = () => {
      const params = new URLSearchParams(window.location.search);
      const addonsParam = params.get('addons');
      if (addonsParam) {
        const ids = addonsParam
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        setSelectedAddons(ids);
        try {
          localStorage.setItem('tl.selectedAddOns', JSON.stringify(ids));
        } catch {
          /* ignore storage errors */
        }
      } else {
        // Fallback to localStorage to keep cart visible across routes
        try {
          const stored = localStorage.getItem('tl.selectedAddOns');
          if (stored) {
            const ids = JSON.parse(stored);
            if (Array.isArray(ids)) setSelectedAddons(ids);
            else setSelectedAddons([]);
          } else {
            setSelectedAddons([]);
          }
        } catch {
          setSelectedAddons([]);
        }
      }
    };

    // Update on mount
    updateAddons();

    // Listen for URL changes
    window.addEventListener('popstate', updateAddons);
    window.addEventListener('storage', updateAddons);

    // Listen for programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      setTimeout(updateAddons, 0);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      setTimeout(updateAddons, 0);
    };

    return () => {
      window.removeEventListener('popstate', updateAddons);
      window.removeEventListener('storage', updateAddons);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  return selectedAddons;
}

function HeaderContent() {
  const { currentTheme, isScrolled } = useHeaderThemeContext();
  const selectedAddons = useSelectedAddons();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const linkClass = ({ isActive }: { isActive: boolean }) => {
    const baseClasses = 'px-4 py-2 rounded-md text-sm font-medium transition-all duration-300';

    if (isActive) {
      return `${baseClasses} bg-primary text-primary-foreground`;
    }

    // Dynamic classes based on current theme
    if (currentTheme === 'dark') {
      return `${baseClasses} text-white/90 hover:text-white hover:bg-white/10`;
    } else {
      return `${baseClasses} text-gray-600 hover:text-blue-600 hover:bg-gray-50`;
    }
  };

  // Mobile menu link classes
  const mobileLinkClass = ({ isActive }: { isActive: boolean }) => {
    const baseClasses =
      'block px-4 py-3 text-base font-medium border-b border-gray-100 transition-all duration-300';

    if (isActive) {
      return `${baseClasses} bg-blue-50 text-blue-600 border-blue-200`;
    }

    return `${baseClasses} text-gray-700 hover:text-blue-600 hover:bg-gray-50`;
  };

  // Clean header classes with glass effect
  const headerClasses = `
    sticky top-0 z-50 w-full border-b backdrop-blur-sm supports-backdrop-filter:bg-background/60 shadow-xs transition-all duration-300
    ${
      currentTheme === 'dark'
        ? 'bg-black/20 border-white/10 text-white'
        : 'bg-white/80 border-gray-200 text-gray-900'
    }
  `.trim();

  // Clean brand text classes
  const brandTextClasses =
    currentTheme === 'dark'
      ? 'font-bold text-xl transition-colors duration-300 text-white'
      : 'font-bold text-xl transition-colors duration-300 text-gray-900';

  const subTextClasses =
    currentTheme === 'dark'
      ? 'text-sm -mt-1 transition-colors duration-300 text-white/70'
      : 'text-sm -mt-1 transition-colors duration-300 text-gray-500';

  // Dynamic "Get Started" button classes
  const ctaButtonClasses =
    currentTheme === 'dark'
      ? 'px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 bg-white text-gray-900 hover:bg-gray-100'
      : 'px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white';

  return (
    <>
      <header className={headerClasses} data-header-theme={currentTheme}>
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-20 items-center justify-between">
            <Link
              to="/"
              className="flex items-center space-x-2 sm:space-x-3 min-w-0 shrink max-w-[60%] xs:max-w-none"
            >
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-linear-to-br from-blue-600 to-indigo-600 flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-sm sm:text-lg">TL</span>
              </div>
              <div className="flex flex-col min-w-0 shrink overflow-hidden">
                <span className={`${brandTextClasses} text-base xs:text-lg sm:text-xl truncate`}>
                  ToolboxLogic
                </span>
                {/* Show abbreviated text on very small screens, full text on xs+ */}
                <span
                  className={`${subTextClasses} text-xs sm:text-sm -mt-1 truncate hidden xs:block`}
                >
                  <span className="xs:hidden">GLA Demo</span>
                  <span className="hidden xs:inline">Landowners Association-Demo</span>
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <NavLink to="/" className={linkClass}>
                Home
              </NavLink>
              <NavLink to="/governance" className={linkClass}>
                Governance
              </NavLink>
              <NavLink to="/pricing" className={linkClass}>
                Pricing
              </NavLink>
              <NavLink to="/faq" className={linkClass}>
                FAQ
              </NavLink>
            </nav>

            <div className="flex items-center space-x-1 xs:space-x-2 sm:space-x-4 shrink-0">
              {/* Get Started Button - Always visible, responsive sizing */}
              <Link
                to="/onboarding"
                className={`${ctaButtonClasses} text-xs sm:text-sm px-2 xs:px-3 sm:px-4 py-2 whitespace-nowrap`}
              >
                <span className="hidden xs:inline">Get Started</span>
                <span className="xs:hidden">Start</span>
              </Link>

              {/* Mobile Menu Button - Larger touch target on small screens */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 sm:p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg z-50">
            <nav className="px-4 sm:px-6 py-4">
              <NavLink
                to="/"
                className={mobileLinkClass}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </NavLink>
              <NavLink
                to="/governance"
                className={mobileLinkClass}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Governance
              </NavLink>
              <NavLink
                to="/pricing"
                className={mobileLinkClass}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </NavLink>
              <NavLink
                to="/faq"
                className={mobileLinkClass}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQ
              </NavLink>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}

export function DemoLayout({ children }: { children: React.ReactNode }) {
  return (
    <HeaderThemeProvider>
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <HeaderContent />
        <main className="flex-1">{children}</main>

        <footer className="border-t bg-slate-50">
          <div className="container max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col items-center justify-between gap-4 py-12 md:h-20 md:flex-row md:py-0">
              <div className="flex flex-col items-center gap-4 md:flex-row md:gap-2">
                <span className="text-center text-sm leading-loose text-slate-600 md:text-left">
                  © {new Date().getFullYear()} ToolboxLogic. Empowering Montana communities.
                </span>
              </div>
              <div className="flex items-center space-x-6">
                <Link
                  to="/terms"
                  className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/privacy"
                  className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </HeaderThemeProvider>
  );
}
